import { DefaultTheme } from 'styled-components';
import media from './media';
import { IFontStyle, IButtonStyle, IAvatarItem } from './styled';

export const rem = (pixelUnit: number, rootFontSize: number = 16) => {
  return pixelUnit / rootFontSize + `rem`;
};

const getFontStyle = ({
  fontStyle = 'normal',
  fontWeight = 400,
  fontSize = 12,
  lineHeight = 1,
}: IFontStyle): string => {
  return `
    white-space: nowrap;
    font-style: ${fontStyle};
    font-weight: ${fontWeight};
    font-size: ${rem(fontSize)};
    line-height: ${typeof lineHeight === 'number' ? rem(lineHeight) : lineHeight};
  `;
};

const getComponentStyle = ({
  width = null,
  borderRadius,
  paddingY,
  paddingX,
  fontStyle = 'normal',
  fontWeight = 400,
  fontSize = 12,
  lineHeight = 1,
}: IButtonStyle): string => {
  return `
    ${width && `width: calc(${width} - ${rem(paddingX * 2)})`};
    padding: ${rem(paddingY)} ${rem(paddingX)};
    border-radius: ${rem(borderRadius)};
    ${getFontStyle({ fontStyle, fontWeight, fontSize, lineHeight })};
 `;
};

const getAvatarStyle = ({
  width,
  height,
  borderRadius,
  borderWidth = 1,
  borderColor,
  padding,
}: IAvatarItem): string => {
  return `
    border-radius: ${rem(borderRadius)};
    // object-fit: contain;
    width: ${rem(width)};
    height: ${rem(height)};
    ${borderColor ? `border: ${rem(borderWidth)} solid ${borderColor}` : ''};
    ${padding && `padding: 0 ${rem(5)}`}
 `;
};

const defaultThemeConfig: DefaultTheme = {
  color: {
    transparent: 'transparent',
    white: '#FFFFFF',
    'gray-800': '#212121',
    'gray-700': '#757575',
    'gray-600': '#A7A7A7',
    'gray-500': '#D3D3D3',
    'gray-400': '#E0E0E0',
    'gray-300': '#EEEEEE',
    'gray-200': '#F5F5F5',
    'gray-100': '#FAFAFA',
    'gray-50': '#BDBDBD',
    'gray-40': '#9E9E9E',
    'primary-100': '#FF3A4A',
    'primary-50': '#FFEBED',
  },
  fontSizes: {
    lg: rem(19),
    md: rem(16),
    sm: rem(12),
  },
  buttonStyles: {
    lg: getComponentStyle({
      fontWeight: 700,
      fontSize: 19,
      lineHeight: '100%',
      paddingY: 18,
      paddingX: 16,
      borderRadius: 12,
    }),
    md: getComponentStyle({
      fontWeight: 700,
      fontSize: 16,
      lineHeight: '100%',
      paddingY: 12,
      paddingX: 10,
      borderRadius: 12,
    }),
    sm: getComponentStyle({
      fontWeight: 700,
      fontSize: 12,
      lineHeight: '100%',
      paddingY: 9,
      paddingX: 16,
      borderRadius: 8,
    }),
    xs: getComponentStyle({
      fontWeight: 700,
      fontSize: 10,
      lineHeight: '100%',
      paddingY: 9,
      paddingX: 16,
      borderRadius: 8,
    }),
  },
  chipStyle: getComponentStyle({
    fontWeight: 500,
    fontSize: 11,
    lineHeight: '100%',
    paddingY: 6,
    paddingX: 8,
    borderRadius: 30,
  }),
  avatarStyles: {
    rectangle: getAvatarStyle({ width: 54, height: 44, borderRadius: 3 }),
    circle: {
      sm: getAvatarStyle({
        width: 36,
        height: 36,
        borderRadius: 36,
        borderWidth: 1,
        borderColor: '#FAFAFA',
      }),
      md: getAvatarStyle({
        width: 44,
        height: 44,
        borderRadius: 44,
        borderWidth: 1,
        borderColor: '#FAFAFA',
      }),
      lg: getAvatarStyle({
        width: 56,
        height: 56,
        borderRadius: 56,
        borderWidth: 1,
        borderColor: '#FAFAFA',
      }),
    },
    square: getAvatarStyle({ width: 64, height: 64, borderRadius: 20 }),
  },
  typography: {
    h1: getFontStyle({ fontWeight: 900, fontSize: 23, lineHeight: 30 }),
    h2: getFontStyle({ fontWeight: 500, fontSize: 23, lineHeight: 30 }),
    h3: getFontStyle({ fontWeight: 500, fontSize: 20, lineHeight: 28 }),
    h4: getFontStyle({ fontWeight: 500, fontSize: 18, lineHeight: 24 }),
    title1: getFontStyle({ fontWeight: 600, fontSize: 16, lineHeight: 24 }),
    title4: getFontStyle({ fontWeight: 500, fontSize: 14, lineHeight: 20 }),
    subtitle1: getFontStyle({ fontWeight: 600, fontSize: 18, lineHeight: 24 }),
    subtitle2: getFontStyle({ fontWeight: 400, fontSize: 18, lineHeight: 24 }),
    subtitle3: getFontStyle({ fontWeight: 600, fontSize: 16, lineHeight: 21 }),
    subtitle4: getFontStyle({ fontWeight: 400, fontSize: 16, lineHeight: 21 }),
    subtitle5: getFontStyle({ fontWeight: 600, fontSize: 14, lineHeight: 19 }),
    subtitle6: getFontStyle({ fontWeight: 400, fontSize: 14, lineHeight: 18 }),
    subtitle7: getFontStyle({ fontWeight: 600, fontSize: 12, lineHeight: 16 }),
    subtitle8: getFontStyle({ fontWeight: 400, fontSize: 12, lineHeight: 16 }),
    subtitle9: getFontStyle({ fontWeight: 400, fontSize: 11, lineHeight: 14 }),
    subtitle10: getFontStyle({ fontWeight: 400, fontSize: 10, lineHeight: 14 }),
  },
  media,
};
export const theme: DefaultTheme = defaultThemeConfig;
